import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AdminLangBar from "./AdminLangBar";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, X } from "lucide-react";
import RichTextEditor from "../../components/RichTextEditor";
import ImageUploader from "../../components/ImageUploader";

const inputCls =
  "w-full bg-transparent border-b border-[#E5DFD5] py-2.5 text-sm focus:outline-none focus:border-[#C5A880]";
const FieldLabel = ({ children }) => (
  <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">{children}</label>
);

function ObjectList({ value, onChange, fields, addLabel = "Add" }) {
  const list = value || [];
  return (
    <div className="space-y-4">
      {list.map((row, i) => (
        <div key={i} className="border border-[#E5DFD5] bg-[#FFFFFF] p-4 space-y-3 relative">
          {fields.map((f) => (
            <div key={f.key}>
              <FieldLabel>{f.label}</FieldLabel>
              {f.textarea ? (
                <textarea
                  value={row[f.key] || ""}
                  onChange={(e) => {
                    const next = [...list];
                    next[i] = { ...next[i], [f.key]: e.target.value };
                    onChange(next);
                  }}
                  rows={2}
                  className={`${inputCls} resize-none`}
                />
              ) : (
                <input
                  value={row[f.key] || ""}
                  onChange={(e) => {
                    const next = [...list];
                    next[i] = { ...next[i], [f.key]: e.target.value };
                    onChange(next);
                  }}
                  className={inputCls}
                />
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => onChange(list.filter((_, idx) => idx !== i))}
            className="absolute top-2 right-2 text-[#73706B] hover:text-red-700 p-1"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          onChange([...list, fields.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {})])
        }
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C5A880] hover:text-[#1A1A1A]"
      >
        <Plus className="w-3 h-3" strokeWidth={1.5} /> {addLabel}
      </button>
    </div>
  );
}

export default function AdminHomeEdit() {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("en");
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setPage(null);
    (async () => {
      try {
        const { data } = await api.get("/admin/pages/home", { params: { lang } });
        setPage(data);
      } catch (_err) {
        toast.error("Failed to load home page");
        navigate("/admin/pages");
      }
    })();
  }, [navigate, lang, reloadTick]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { key: _, ...payload } = page;
      await api.put("/admin/pages/home", payload, { params: { lang } });
      toast.success("Home page saved");
    } catch (_err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!page)
    return (
      <div className="p-10">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A880]" strokeWidth={1.5} />
      </div>
    );

  const upd = (k) => (v) => setPage({ ...page, [k]: v });

  return (
    <div className="p-10 lg:p-16 max-w-[1000px]" data-testid="admin-home-edit">
      <Link to="/admin/pages" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A] mb-8">
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> All Pages
      </Link>
      <h1 className="text-4xl lg:text-5xl font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Home Page
      </h1>
      <p className="text-sm text-[#73706B] mb-12">All the content blocks on the homepage.</p>

      <AdminLangBar lang={lang} onChange={setLang} kind="page" docKey={"home"} disabled={false} onTranslated={() => setReloadTick((n) => n + 1)} />

      <form onSubmit={save} className="space-y-12">
        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Hero</h2>
          <ImageUploader label="Hero image" value={page.hero_image || ""} onChange={upd("hero_image")} testId="home-hero-image" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FieldLabel>Eyebrow</FieldLabel>
              <input value={page.eyebrow || ""} onChange={(e) => setPage({ ...page, eyebrow: e.target.value })} className={inputCls} data-testid="home-field-eyebrow" />
            </div>
          </div>
          <div>
            <FieldLabel>Main heading (use a line break for the italic accent line)</FieldLabel>
            <textarea value={page.heading || ""} onChange={(e) => setPage({ ...page, heading: e.target.value })} rows={2} className={`${inputCls} resize-none`} data-testid="home-field-heading" />
          </div>
          <div>
            <FieldLabel>Hero subtitle</FieldLabel>
            <textarea value={page.hero_subtitle || ""} onChange={(e) => setPage({ ...page, hero_subtitle: e.target.value })} rows={2} className={`${inputCls} resize-none`} data-testid="home-field-subtitle" />
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Intro section</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FieldLabel>Intro eyebrow</FieldLabel>
              <input value={page.intro_eyebrow || ""} onChange={(e) => setPage({ ...page, intro_eyebrow: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div>
            <FieldLabel>Intro heading (HTML &lt;em&gt; allowed)</FieldLabel>
            <textarea value={page.intro_heading || ""} onChange={(e) => setPage({ ...page, intro_heading: e.target.value })} rows={2} className={`${inputCls} resize-none`} />
          </div>
          <div>
            <FieldLabel>Intro body</FieldLabel>
            <div data-testid="home-field-intro"><RichTextEditor value={page.intro || ""} onChange={upd("intro")} /></div>
          </div>
          <div>
            <FieldLabel>Stats (Number + Label)</FieldLabel>
            <ObjectList
              value={page.stats}
              onChange={(v) => setPage({ ...page, stats: v })}
              fields={[
                { key: "k", label: "Number / Value" },
                { key: "l", label: "Label" },
              ]}
              addLabel="Add stat"
            />
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Why buy here</h2>
          <div>
            <FieldLabel>Heading</FieldLabel>
            <input value={page.why_buy_heading || ""} onChange={(e) => setPage({ ...page, why_buy_heading: e.target.value })} className={inputCls} />
          </div>
          <ObjectList
            value={page.why_buy}
            onChange={(v) => setPage({ ...page, why_buy: v })}
            fields={[
              { key: "title", label: "Title" },
              { key: "body", label: "Body", textarea: true },
            ]}
            addLabel="Add reason"
          />
        </section>

        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Buyer&rsquo;s guide</h2>
          <div>
            <FieldLabel>Heading (HTML &lt;em&gt; allowed)</FieldLabel>
            <input value={page.buyer_guide_heading || ""} onChange={(e) => setPage({ ...page, buyer_guide_heading: e.target.value })} className={inputCls} />
          </div>
          <ObjectList
            value={page.buyer_guide}
            onChange={(v) => setPage({ ...page, buyer_guide: v })}
            fields={[
              { key: "number", label: "Number (e.g. 01)" },
              { key: "title", label: "Title" },
              { key: "body", label: "Body", textarea: true },
            ]}
            addLabel="Add step"
          />
        </section>

        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— FAQs</h2>
          <ObjectList
            value={page.faqs}
            onChange={(v) => setPage({ ...page, faqs: v })}
            fields={[
              { key: "q", label: "Question" },
              { key: "a", label: "Answer", textarea: true },
            ]}
            addLabel="Add FAQ"
          />
        </section>

        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Contact block</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FieldLabel>Eyebrow</FieldLabel>
              <input value={page.contact_eyebrow || ""} onChange={(e) => setPage({ ...page, contact_eyebrow: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div>
            <FieldLabel>Heading (HTML &lt;em&gt; allowed)</FieldLabel>
            <input value={page.contact_heading || ""} onChange={(e) => setPage({ ...page, contact_heading: e.target.value })} className={inputCls} />
          </div>
          <div>
            <FieldLabel>Intro</FieldLabel>
            <RichTextEditor value={page.contact_intro || ""} onChange={upd("contact_intro")} />
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— SEO</h2>
          <div>
            <FieldLabel>SEO title</FieldLabel>
            <input value={page.seo?.title || ""} onChange={(e) => setPage({ ...page, seo: { ...(page.seo || {}), title: e.target.value } })} className={inputCls} />
          </div>
          <div>
            <FieldLabel>SEO description</FieldLabel>
            <textarea value={page.seo?.description || ""} onChange={(e) => setPage({ ...page, seo: { ...(page.seo || {}), description: e.target.value } })} rows={2} className={`${inputCls} resize-none`} />
          </div>
        </section>

        <div className="flex items-center gap-4 border-t border-[#E5DFD5] pt-8">
          <button type="submit" disabled={saving} data-testid="home-save" className="bg-[#1A1A1A] text-[#F9F6F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A880] disabled:opacity-60 transition-colors inline-flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
            {saving ? "Saving" : "Save Home Page"}
          </button>
          <Link to="/admin/pages" className="text-[11px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A]">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
